import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { dictionary, initDictionary, resetDictionary, updateDictionary, applyPreset } from "$stores/dictionary";
import { PRESETS } from "$stores/dictionary-presets";

import DictionaryItem from "./components/dictionary-item";

import "./dictionary.scss";
import classNames from "classnames";

const DictionaryAdmin = () => {
    const [newDictionary, setNewDictionary] = useState([]);
    const [changes, setChanges] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showChanges, setShowChanges] = useState(true);
    const [selectedPreset, setSelectedPreset] = useState("");
    const [isApplyingPreset, setIsApplyingPreset] = useState(false);

    // dictionary is a module-level reference (not React state), so this runs once on mount.
    // Post-apply sync is handled explicitly in handleApplyPreset via setNewDictionary.
    useEffect(() => {
        if (dictionary) {
            setNewDictionary(Object.keys(dictionary).map((key) => ({ key: key, value: dictionary[key] })));
        }
    }, [dictionary]);

    const save = async () => {
        setIsSaving(true);
        const changesToSave = changes.reduce((acc, change) => ({ ...acc, [change.key]: { term: change.value } }), {});
        await updateDictionary(changesToSave);
        await resetDictionary();
        await initDictionary();
        setChanges([]);
        setIsSaving(false);
    };

    const handleApplyPreset = async () => {
        const preset = PRESETS.find((p) => p.label === selectedPreset);
        if (!preset) return;
        if (!window.confirm(`This will overwrite all dictionary terms with the "${preset.label}" preset. Continue?`)) return;
        setIsApplyingPreset(true);
        try {
            await applyPreset(preset);
            setNewDictionary(Object.keys(dictionary).map((key) => ({ key, value: dictionary[key] })));
            setChanges([]);
            setSelectedPreset("");
        } finally {
            setIsApplyingPreset(false);
        }
    };

    return (
        <div>
            <h1>Dictionary Admin</h1>
            <div className="preset-selector">
                <select value={selectedPreset} onChange={(e) => setSelectedPreset(e.target.value)}>
                    <option value="">-- Select Preset --</option>
                    {PRESETS.map((p) => (
                        <option key={p.label} value={p.label}>
                            {p.label}
                        </option>
                    ))}
                </select>
                <button
                    className="button -secondary -small"
                    onClick={handleApplyPreset}
                    disabled={!selectedPreset || isApplyingPreset}
                >
                    {isApplyingPreset ? "Applying..." : "Apply Preset"}
                </button>
            </div>
            <div className="dictionary-edit">
                <div>
                    {newDictionary.map((item) => {
                        return (
                            <DictionaryItem
                                key={item.key}
                                item={item}
                                onChange={(key, value) => {
                                    setNewDictionary(
                                        newDictionary.map((item) => {
                                            if (item.key === key) {
                                                return { key: key, value: value };
                                            } else {
                                                return item;
                                            }
                                        })
                                    );
                                    setChanges((prev) => {
                                        const exists = prev.find((c) => c.key === key);
                                        if (exists) {
                                            return prev.map((c) => c.key === key ? { ...c, value } : c);
                                        }
                                        return [...prev, { key: key, value: value, originalValue: dictionary[key] }];
                                    });
                                }}
                            />
                        );
                    })}
                </div>
            </div>
            <article id="dictionary-changes" className={classNames({ "-open": showChanges })}>
                {changes.length > 0 && (
                    <>
                        <div className="flex-header">
                            <button
                                className="button -text"
                                onClick={() => {
                                    setShowChanges(!showChanges);
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronDown} />
                            </button>
                            <h3>Changes ({changes.length})</h3>
                            <button className="button -secondary -small" onClick={save} disabled={isSaving}>
                                Save
                            </button>
                        </div>

                        <div className="change-wrapper">
                            <div className="change-list">
                                {changes.map((change) => {
                                    return (
                                        <div key={`change_${change.key}`} className="change-item">
                                            <b>{change.key}</b>:<br />
                                            {change.originalValue || "::blank::"} <span>--&gt;</span> {change.value}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </article>
        </div>
    );
};

export default DictionaryAdmin;
